import { useCallback, useEffect, useState } from "react";
import { api } from "../api";
import { fmtMonth, fmtSigned, fmtUnits, scopeLabel, STATUS_LABEL, timeAgo } from "../format";
import type { Board, FunctionDef, Impact, Intent, Scope, WorkType } from "../types";
import RunActivity, { useRunEvents } from "./RunActivity";
import ScopePicker from "./ScopePicker";
import { ColumnChart, LineChart } from "./charts";
import { IconButton } from "./Icons";
import RunSummary from "./RunSummary";
import { AnswerPanel } from "./AnswerView";

type Tab = "activity" | "result" | "impact";

interface Props {
  board: Board;
  intentId: string;
  workTypes: Record<string, WorkType>;
  functions: FunctionDef[];
  scenario: string;
  onClose: () => void;
  onChanged: () => void;
  onDeleted: () => void;
  onReuse: (intent: Intent) => void;
  notify: (msg: string) => void;
}

export default function IntentDrawer({ board, intentId, workTypes, functions, scenario, onClose, onChanged, onDeleted, onReuse, notify }: Props) {
  const [intent, setIntent] = useState<Intent | null>(null);
  const [activeRun, setActiveRun] = useState<string | null>(null);
  const [runIdx, setRunIdx] = useState<number>(-1);
  const [tab, setTab] = useState<Tab>("activity");
  const [editing, setEditing] = useState(false);
  const [draftText, setDraftText] = useState("");
  const [draftScope, setDraftScope] = useState<Scope>({ family: "", group1: "", group2: "", skus: [] });
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const data = await api.intent(board.id, intentId);
    setIntent(data.intent);
    setActiveRun(data.active_run);
    setRunIdx(data.intent.runs.length - 1);
    if (data.intent.sample) setTab("result");
    return data;
  }, [board.id, intentId]);

  useEffect(() => { load().catch((e) => notify(e.message)); }, [load, notify]);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && !editing && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, editing]);

  const run = intent && runIdx >= 0 ? intent.runs[runIdx] : null;
  const isLive = !!run && run.run_id === activeRun;
  const events = useRunEvents(run?.run_id ?? null, isLive, run?.events, () => {
    load().then((d) => {
      onChanged();
      const last = d.intent.runs[d.intent.runs.length - 1];
      if (last && (last.status === "executed" || last.status === "invalid" || last.status === "failed")) setTab("result");
    }).catch(() => undefined);
  });

  if (!intent) {
    return <aside className="drawer" aria-label="Intent"><div className="drawer-body"><div className="spinner" /></div></aside>;
  }

  const wt = workTypes[intent.work_type];
  const running = intent.status === "queued" || intent.status === "running";

  const startRun = async () => {
    setBusy(true);
    try {
      await api.runIntent(board.id, intent.id);
      await load();
      setTab("activity");
      onChanged();
    } catch (e) {
      notify((e as Error).message);
    } finally {
      setBusy(false);
    }
  };
  const cancelRun = async () => {
    try { await api.cancelIntent(board.id, intent.id); notify("Cancel requested. The run stops at its next step."); }
    catch (e) { notify((e as Error).message); }
  };
  const saveEdit = async () => {
    setBusy(true);
    try {
      await api.updateIntent(board.id, intent.id, { text: draftText, scope: draftScope });
      setEditing(false);
      await load();
      onChanged();
    } catch (e) {
      notify((e as Error).message);
    } finally {
      setBusy(false);
    }
  };
  const remove = async () => {
    if (!window.confirm(`Delete ${intent.id}? Rows it already wrote stay in “${scenario}”.`)) return;
    try { await api.deleteIntent(board.id, intent.id); onDeleted(); } catch (e) { notify((e as Error).message); }
  };

  return (
    <aside className="drawer" aria-label={`Intent ${intent.id}`}>
      <div className="drawer-head">
        <div className="row">
          <span className={`status ${intent.status}`}><span className="dot" />{STATUS_LABEL[intent.status] ?? intent.status}</span>
          <span className="mono muted">{intent.id}</span>
          <span className="grow" />
          <IconButton icon="close" label="Close" className="btn ghost sm" onClick={onClose} />
        </div>
        <div className="small muted">
          {intent.work_type === "auto" ? "Type of work and scope are inferred from the text when it runs"
            : [functions.find((f) => f.id === wt?.function)?.label, wt?.label].filter(Boolean).join(" · ")}
          {wt && !wt.writes ? " · read-only" : ""}
          {intent.classification ? ` · inferred ${intent.classification.method === "llm" ? "by the model" : "by rules"}` : ""}
        </div>
        {editing ? (
          <div style={{ display: "grid", gap: 10 }}>
            <textarea className="input" rows={4} value={draftText} onChange={(e) => setDraftText(e.target.value)} autoFocus />
            <ScopePicker value={draftScope} onChange={setDraftScope} />
            <div className="row" style={{ justifyContent: "flex-end" }}>
              <button className="btn ghost" onClick={() => setEditing(false)}>Cancel</button>
              <button className="btn primary" disabled={busy || !draftText.trim()} onClick={saveEdit}>Save</button>
            </div>
          </div>
        ) : (
          <>
            <div style={{ fontSize: 15, whiteSpace: "pre-wrap" }}>{intent.text}</div>
            <div className="row" style={{ flexWrap: "wrap" }}>
              <span className="chip">Scope: {scopeLabel(intent.scope)}</span>
              <span className="chip">By {intent.created_by} · {timeAgo(intent.created_at)}</span>
            </div>
            {intent.sample && (
              <div className="callout warn small">Sample intent, shown to illustrate the canvas. It wasn't run against Kinaxis and can't be run.</div>
            )}
            <div className="row" style={{ flexWrap: "wrap" }}>
              {intent.sample || intent.kind === "upload" ? null : running ? (
                <IconButton icon="stop" label="Cancel run" className="btn danger" onClick={cancelRun} />
              ) : (
                <IconButton icon={intent.runs.length ? "rerun" : "run"} label={intent.runs.length ? "Run again" : "Run"}
                            className="btn primary" onClick={startRun} disabled={busy} />
              )}
              {intent.kind === "upload" ? (
                <span className="small muted">Mass upload: push or discard it on the card. To change it, upload the file again.</span>
              ) : (
                <>
                  <IconButton icon="edit" label="Edit" className="btn" disabled={running || intent.sample}
                              onClick={() => { setDraftText(intent.text); setDraftScope(intent.scope); setEditing(true); }} />
                  <IconButton icon="reuse" label="Reuse in Today: start a new intent with this text" className="btn"
                              onClick={() => onReuse(intent)} />
                </>
              )}
              <IconButton icon="trash" label="Delete" className="btn ghost danger" disabled={running} onClick={remove} />
              <span className="grow" />
              {intent.runs.length > 1 && (
                <select className="input" style={{ width: "auto" }} value={runIdx} aria-label="Run"
                        onChange={(e) => setRunIdx(Number(e.target.value))}>
                  {intent.runs.map((r, idx) => (
                    <option key={r.run_id} value={idx}>Run {idx + 1} · {STATUS_LABEL[r.status] ?? r.status}</option>
                  ))}
                </select>
              )}
            </div>
            {wt?.writes && !running && !intent.sample && (
              <div className="small muted">Running writes to <b>{scenario}</b>. Each row carries {intent.id} in its id.</div>
            )}
          </>
        )}
      </div>

      <div className="drawer-tabs" role="tablist">
        {(["activity", "result", "impact"] as Tab[]).map((t) => (
          <button key={t} className="tab" role="tab" aria-selected={tab === t} onClick={() => setTab(t)}
                  disabled={t === "impact" && (!wt?.writes || intent.sample)}>
            {t === "activity" ? "Activity" : t === "result" ? "Result" : "Impact"}
          </button>
        ))}
      </div>

      <div className="drawer-body">
        {!run ? (
          <div className="empty">Not run yet. Press <b>Run</b> to send it to the agent.</div>
        ) : tab === "activity" && intent.sample ? (
          <div className="empty">Sample intents have no activity log.</div>
        ) : tab === "activity" ? (
          <RunActivity events={events} live={isLive} />
        ) : tab === "result" ? (
          <RunResult intent={intent} runIdx={runIdx} />
        ) : (
          <ImpactPanel boardId={board.id} intentId={intent.id} scenario={scenario} refreshKey={run.finished_at ?? ""} />
        )}
      </div>
    </aside>
  );
}

const moneyFmt = new Intl.NumberFormat(undefined, { style: "currency", currency: "USD", maximumFractionDigits: 0 });
const fmtSignedMoney = (v: number) => `${v > 0 ? "+" : v < 0 ? "−" : ""}${moneyFmt.format(Math.abs(v))}`;

function RunResult({ intent, runIdx }: { intent: Intent; runIdx: number }) {
  const run = intent.runs[runIdx];
  if (run.status === "queued" || run.status === "running") return <div className="muted">The result appears when the run ends.</div>;
  const c = run.classification;
  const tone = run.status === "executed" ? "good" : run.status === "failed" ? "bad" : "warn";
  const writes = run.writes ?? [];
  const usage = run.usage ?? {};
  return (
    <>
      <div className={`callout ${tone}`}>
        <span className={`status ${run.status}`}><span className="dot" />{STATUS_LABEL[run.status] ?? run.status}</span>
        {run.summary ? <RunSummary text={run.summary} /> : <div>No summary.</div>}
        {run.capability_gap && <div className="small"><b>What's missing:</b> {run.capability_gap}</div>}
        {run.value_delta ? (
          <div className="small">
            <b>Demand-plan value:</b> <span className={run.value_delta >= 0 ? "pos" : "neg"}>{fmtSignedMoney(run.value_delta)}</span>
            <span className="muted"> (units × consensus unit price)</span>
          </div>
        ) : null}
      </div>
      {(run.views?.length ?? 0) > 0 && (
        <AnswerPanel boardId={intent.board_id} intentId={intent.id} runId={run.run_id} refreshKey={run.finished_at} showSummary={false} />
      )}

      {c && (
        <div>
          <div className="section-title">Classification</div>
          <table className="data">
            <tbody>
              <tr><td className="muted">Type of work</td><td>{c.work_type} · {c.function}</td></tr>
              <tr><td className="muted">Scope</td><td>{scopeLabel(c.scope)}</td></tr>
              <tr><td className="muted">Confidence</td><td>{c.confidence}</td></tr>
              <tr><td className="muted">Why</td><td>{c.rationale}</td></tr>
              <tr><td className="muted">Method</td><td>{c.method === "llm" ? "model" : "keyword rules"}</td></tr>
            </tbody>
          </table>
        </div>
      )}

      {writes.length > 0 && (
        <div>
          <div className="section-title">Written to {writes[0].scenario}</div>
          {writes.map((w, i) => (
            <div key={i} style={{ display: "grid", gap: 6, marginBottom: 10 }}>
              <div className="small ink2">
                {fmtUnits(w.records_written)} rows in <span className="mono">{w.target_table}</span> · {w.sku_count} SKUs · net {fmtSigned(w.qty_total)}
              </div>
              <table className="data">
                <thead><tr><th>Month</th><th className="num">Net change</th></tr></thead>
                <tbody>
                  {Object.entries(w.qty_by_month).sort().map(([m, q]) => (
                    <tr key={m}><td>{fmtMonth(m)}</td><td className={`num ${q >= 0 ? "pos" : "neg"}`}>{fmtSigned(q)}</td></tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}
        </div>
      )}

      {(run.insights?.length ?? 0) > 0 && (
        <div>
          <div className="section-title">Findings</div>
          <div style={{ display: "grid", gap: 8 }}>
            {run.insights!.map((ins, i) => (
              <div className="callout" key={i}>
                <div className="row">
                  <b>{ins.title || ins.identifier || ins.anomaly_type || `Finding ${i + 1}`}</b>
                  <span className="grow" />
                  {ins.severity && <span className="chip">{ins.severity}</span>}
                </div>
                {ins.signals && <div className="small ink2">{ins.signals}</div>}
                {ins.recommendation && <div className="small"><b>Recommendation:</b> {ins.recommendation}</div>}
                {ins.affected_skus && (
                  <div className="small muted">SKUs: {Array.isArray(ins.affected_skus) ? ins.affected_skus.join(", ") : ins.affected_skus}</div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="small muted">
        {[
          run.turns != null ? `${run.turns} agent steps` : "",
          run.duration_sec != null ? `${run.duration_sec}s` : "",
          usage.inputTokens ? `${fmtUnits(usage.inputTokens)} input tokens (${fmtUnits(usage.cacheReadInputTokens)} from cache), ${fmtUnits(usage.outputTokens)} output` : "",
          run.started_by ? `run by ${run.started_by}` : "",
        ].filter(Boolean).join(" · ")}
      </div>
    </>
  );
}

function ImpactPanel({ boardId, intentId, scenario, refreshKey }: { boardId: string; intentId: string; scenario: string; refreshKey: string }) {
  const [impact, setImpact] = useState<Impact | null>(null);
  const [error, setError] = useState("");
  const [asTable, setAsTable] = useState(false);
  useEffect(() => {
    setImpact(null);
    api.impact(boardId, intentId).then(setImpact).catch((e) => setError(e.message));
  }, [boardId, intentId, refreshKey]);

  if (error) return <div className="error-text">{error}</div>;
  if (!impact) return <div className="row muted"><div className="spinner" /> Reading from Kinaxis…</div>;

  const months = impact.written.by_month.reduce<Record<string, number>>((acc, r) => {
    acc[r.month] = (acc[r.month] ?? 0) + r.quantity;
    return acc;
  }, {});
  const monthRows = Object.entries(months).sort().map(([label, value]) => ({ label, value }));
  const s = impact.scope_monthly;

  return (
    <>
      <div className="callout">
        <div className="small ink2">Rows in <b>{scenario}</b> whose id is {intentId}</div>
        <div style={{ fontSize: 22, fontWeight: 650 }}>{fmtSigned(impact.written.total)} units</div>
        <div className="small muted">{fmtUnits(impact.written.rows_found)} month × SKU rows found · read live from Kinaxis</div>
      </div>
      <div>
        <div className="section-title">Written by this intent, by month</div>
        <ColumnChart rows={monthRows} name="net change" />
      </div>
      <div>
        <div className="row">
          <div className="section-title grow" style={{ margin: 0 }}>Scope consensus: {scenario}{impact.baseline ? ` vs ${impact.baseline}` : ""}</div>
          <button className="btn ghost sm" onClick={() => setAsTable((v) => !v)}>{asTable ? "Chart" : "Table"}</button>
        </div>
        <div className="small muted" style={{ marginBottom: 6 }}>Includes every intent that touched these SKUs, not only this one.</div>
        {asTable ? (
          <table className="data">
            <thead><tr><th>Month</th><th className="num">{scenario}</th>{impact.baseline && <><th className="num">{impact.baseline}</th><th className="num">Δ</th></>}</tr></thead>
            <tbody>{s.map((p) => (
              <tr key={p.month}><td>{fmtMonth(p.month)}</td><td className="num">{fmtUnits(p.target)}</td>
                {impact.baseline && <><td className="num">{fmtUnits(p.baseline)}</td><td className={`num ${(p.delta ?? 0) >= 0 ? "pos" : "neg"}`}>{fmtSigned(p.delta)}</td></>}
              </tr>))}
            </tbody>
          </table>
        ) : (
          <LineChart labels={s.map((p) => p.month)} series={[
            { name: "Target", color: "var(--series-target)", values: s.map((p) => p.target) },
            ...(impact.baseline ? [{ name: "Baseline", color: "var(--series-base)", dashed: true, values: s.map((p) => p.baseline) }] : []),
          ]} />
        )}
      </div>
      {impact.written.by_sku.length > 0 && (
        <div>
          <div className="section-title">Largest SKU changes</div>
          <table className="data">
            <thead><tr><th>SKU</th><th className="num">Net change</th></tr></thead>
            <tbody>{impact.written.by_sku.slice(0, 12).map((r) => (
              <tr key={r.sku}><td className="mono">{r.sku}</td><td className={`num ${r.quantity >= 0 ? "pos" : "neg"}`}>{fmtSigned(r.quantity)}</td></tr>
            ))}</tbody>
          </table>
        </div>
      )}
    </>
  );
}
